import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp, collection, addDoc, deleteField, getDocs } from "firebase/firestore";
import Spinner from './Spinner.js';
import { ArrowLeft, Edit2, CheckCircle, Copy, Printer, Send, Clock, Trash2, ChevronDown, FileText, Truck } from 'lucide-react';
import useClickOutside from './hooks/useClickOutside.js';

import { storage, ref, uploadBytes, getDownloadURL } from './firebaseUtils.js';
import MICPreviewContent from './MICPreviewContent.js';

const MICPreview = ({ db, user, micId: micIdProp, embedded = false, onProceedToQC, hideCompletionDetails = false }) => {
  const params = useParams();
  const micId = micIdProp || params.micId || params.id;
  const navigate = useNavigate();
  const [mic, setMic] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showMoreDropdown, setShowMoreDropdown] = useState(false);
  const moreDropdownRef = useClickOutside(() => setShowMoreDropdown(false));
  const printRef = useRef();

  const [completionRemarks, setCompletionRemarks] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const docRef = doc(db, "inwards", micId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          const micData = { 
            id: docSnap.id, 
            ...data, 
            components: data.micComponents || [], 
            completionRemarks: data.completionRemarks || '', 
            completionFiles: data.completionFiles || [] 
          };
          setMic(micData);
          setCompletionRemarks(data.mic_completionRemarks || '');

          // Fetch Customer details for address
          if (micData.customerID) {
            const custRef = doc(db, "customers", micData.customerID);
            const custSnap = await getDoc(custRef);
            if (custSnap.exists()) {
              setCustomer(custSnap.data());
            }
          }

          // Fetch Warehouses for address display
          const whSnap = await getDocs(collection(db, "warehouses"));
          setWarehouses(whSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        } else {
          alert("MIC not found");
          navigate('/ops/mic');
        }
      } catch (error) {
        console.error("Error fetching MIC:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [db, micId, navigate]);

  const handleStatusUpdate = async (newStatus, confirmMsg, successMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    
    setIsUpdating(true);
    try {
      const timestamp = serverTimestamp();
      const userName = user.displayName || user.email;
      const micRef = doc(db, "inwards", micId);

      if (newStatus === 'mic_completed') {
        let uploadedFiles = mic.mic_completionFiles || [];
        if (selectedFiles.length > 0) {
          uploadedFiles = await Promise.all(
            selectedFiles.map(async (file) => {
              const safeName = (file.name || 'file').replace(/[^\w.\-() ]+/g, '_');
              const path = `mic_completion/${micId}/${Date.now()}_${safeName}`;
              const storageRef = ref(storage, path);
              await uploadBytes(storageRef, file);
              const url = await getDownloadURL(storageRef);
              return { name: file.name, type: file.type, size: file.size, url };
            })
          );
        }

        const updatePayload = {
          status: 'mic_completed',
          micStatus: 'mic_completed',
          mic_completionRemarks: completionRemarks || mic.mic_completionRemarks || '',
          mic_completionFiles: uploadedFiles,
          updatedAt: timestamp,
          updatedBy: userName
        };
        
        await updateDoc(micRef, updatePayload);

        // Update local state to reflect the changes immediately
        setMic(prev => ({ 
          ...prev, 
          status: 'mic_completed',
          micStatus: 'mic_completed',
          mic_completionRemarks: completionRemarks || prev.mic_completionRemarks || '',
          mic_completionFiles: uploadedFiles
        }));

        alert(successMsg);
        return;
      }

      await updateDoc(micRef, {
        status: newStatus,
        updatedAt: timestamp,
        updatedBy: userName
      });

      setMic(prev => ({ ...prev, status: newStatus }));
      alert(successMsg);
    } catch (error) {
      console.error("Error updating MIC status:", error);
      alert("Error: " + error.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleClone = async () => {
    const clonedData = { 
      ...mic, 
      inwardNo: mic.inwardNo + '-CLONE',
      status: 'mic_draft',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    delete clonedData.id;
    
    try {
      const docRef = await addDoc(collection(db, "inwards"), clonedData);
      alert("MIC Cloned as Draft.");
      navigate(`/ops/mic/edit/${docRef.id}`);
    } catch (e) {
      alert("Error cloning: " + e.message);
    }
  };

  const handleDiscardMIC = async () => {
    if (!window.confirm("Discard this MIC progress? This will reset the status back to 'Pickup Ready' and wipe the inward checklist data. The original Pickup Challan will remain safe.")) return;
    
    setIsUpdating(true);
    try {
      const docRef = doc(db, "inwards", micId);
      await updateDoc(docRef, {
        status: 'pc_ready',
        micStatus: deleteField(),
        micComponents: deleteField(),
        micNo: deleteField(),
        micDate: deleteField(),
        typeOfMIC: deleteField(),
        shippingAddress: deleteField(),
        warehouse: deleteField(),
        preparedBy: deleteField(),
        quantityVerifiedBy: deleteField(),
        vehicleNumber: deleteField(),
        vehicleInTime: deleteField(),
        vehicleOutTime: deleteField(),
        vehicleDateTime: deleteField(),
        verifiedByFinance: deleteField(),
        declarationDate: deleteField(),
        updatedAt: serverTimestamp()
      });
      alert("MIC Discarded. Document reverted to Pickup stage.");
      navigate('/ops/pickup-challan');
    } catch (e) {
      alert("Error deleting: " + e.message);
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current.innerHTML;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>MIC ${mic.micNo}</title>
          <style>
            body { font-family: Arial, sans-serif; color: #000; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
            td, th { border: 1px solid #000 !important; padding: 8px; text-align: left; font-size: 13px; }
            th { font-weight: bold; background-color: #f2f2f2; }
            @page { margin: 1cm; }
          </style>
        </head>
        <body>${printContent}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 250);
  };

  const handleProceedToQC = async () => {
    if (!window.confirm("Move this MIC to QC Pending?")) return;

    setIsUpdating(true);
    try {
      const updatedMic = {
        ...mic,
        status: 'qc_pending',
        micStatus: 'qc_pending'
      };

      await updateDoc(doc(db, "inwards", micId), {
        status: 'qc_pending',
        micStatus: 'qc_pending',
        updatedAt: serverTimestamp(),
        updatedBy: user.displayName || user.email
      });

      setMic(updatedMic);

      if (typeof onProceedToQC === 'function') {
        onProceedToQC(updatedMic);
        return;
      }

      navigate(`/ops/inbound-qc/${micId}`);
    } catch (error) {
      console.error("Error moving MIC to QC Pending:", error);
      alert("Error: " + error.message);
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '20px' }}>
      {/* Header Buttons */}
      {!embedded && (
        <div className="no-print" style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <button onClick={() => navigate('/ops/mic')} style={backBtnStyle}>
              <ArrowLeft size={20} />
            </button>
            <h2 style={{ margin: 0 }}>MIC Preview: {mic.micNo}</h2>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            {(mic.status === 'mic_draft') && (
              <>
                <button onClick={() => navigate(`/ops/mic/edit/${mic.id}`)} style={editBtnStyle}>
                  <Edit2 size={18} /> Edit
                </button>
                <button 
                  onClick={() => handleStatusUpdate('mic_completed', "Confirm this MIC? This will complete the MIC and lock it for editing.", "MIC Completed Successfully")} 
                  disabled={isUpdating} 
                  style={confirmBtnStyle}
                >
                  <Send size={18} /> Confirm MIC
                </button>
              </>
            )}

            {mic.status === 'mic_completed' && (
              <button 
                onClick={handleProceedToQC}
                disabled={isUpdating}
                style={confirmBtnStyle}
              >
                <Truck size={18} /> Proceed to QC
              </button>
            )}

            <button onClick={handlePrint} style={secondaryBtnStyle}>
              <Printer size={18} /> Export PDF
            </button>

            <div style={{ position: 'relative' }} ref={moreDropdownRef}>
              <button onClick={() => setShowMoreDropdown(!showMoreDropdown)} style={secondaryBtnStyle}>
                More <ChevronDown size={18}/>
              </button>
              {showMoreDropdown && (
                <div style={{ position: 'absolute', top: '100%', right: 0, backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', zIndex: 100, minWidth: '160px', marginTop: '5px' }}>
                  <button onClick={handleClone} style={dropdownItemStyle}><Copy size={16} /> Clone MIC</button>
                  <button onClick={handleDiscardMIC} style={{ ...dropdownItemStyle, color: '#ef4444' }}><Trash2 size={16} /> Discard MIC</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Status Banner */}
      <div style={statusBannerStyle(mic.status)}>
        {mic.status === 'mic_draft' ? <Clock size={20} /> : <CheckCircle size={20} />}
        <span>MIC is currently in <strong>{mic.status?.replace('_', ' ').toUpperCase()}</strong> status.</span>
      </div>

      <MICPreviewContent
        micData={mic}
        customer={customer}
        warehouses={warehouses}
        printRef={printRef}
      />

      {/* Completion Details from PC/MIC (Read-Only) */}
      {!hideCompletionDetails && (mic.mic_completionRemarks || (mic.mic_completionFiles && mic.mic_completionFiles.length > 0)) && (
        <div style={{ marginTop: '20px', backgroundColor: 'white', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '800', color: '#1e293b' }}>
            MIC Completion Details
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#475569', marginBottom: '8px' }}>Completion Remarks</div>
              <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px', minHeight: '90px', whiteSpace: 'pre-wrap', color: '#0f172a', fontSize: '13px' }}>
                {mic.mic_completionRemarks || '—'}
              </div>
            </div>

            <div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#475569', marginBottom: '8px' }}>Attached Files</div>
              {(mic.mic_completionFiles && mic.mic_completionFiles.length > 0) ? (
                <div style={{ display: 'grid', gap: '8px' }}>
                  {mic.mic_completionFiles.map((f, idx) => (
                    <a
                      key={`${f.url || f.name || 'file'}_${idx}`}
                      href={f.url}
                      target="_blank"
                      rel="noreferrer"
                      style={{ 
                        display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', 
                        border: '1px solid #e2e8f0', borderRadius: '10px', textDecoration: 'none', 
                        color: '#0f172a', backgroundColor: '#ffffff',
                        transition: 'background-color 0.2s ease-in-out',
                        '&:hover': { backgroundColor: '#f8fafc' }
                      }}
                    >
                      <FileText size={18} color="#64748b" />
                      <div style={{ overflow: 'hidden' }}>
                        <div style={{ fontSize: '13px', fontWeight: '700', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name || 'Attachment'}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{f.type || 'file'}</div>
                      </div>
                    </a>
                  ))}
                </div>
              ) : (
                <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px', color: '#64748b', fontSize: '13px' }}>
                  No files attached.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style dangerouslySetInnerHTML={{ __html: `@media print { .no-print { display: none !important; } }`}} />
    </div>
  );
};

// --- STYLES ---
const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', padding: '10px 0', borderBottom: '1px solid #e2e8f0' };
const backBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' };
const editBtnStyle = { backgroundColor: '#2563eb', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' };
const confirmBtnStyle = { backgroundColor: '#059669', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' };
const secondaryBtnStyle = { backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db', padding: '8px 16px', borderRadius: '6px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' };
const dropdownItemStyle = { width: '100%', padding: '10px 15px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid #f3f4f6' };
const statusBannerStyle = (status) => ({
  backgroundColor: status === 'mic_draft' ? '#fffbeb' : '#f0fdf4',
  color: status === 'mic_draft' ? '#b45309' : '#166534',
  padding: '12px 20px',
  borderRadius: '8px',
  marginBottom: '20px',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  fontSize: '14px',
  border: `1px solid ${status === 'mic_draft' ? '#fef3c7' : '#bbf7d0'}`
});

export default MICPreview;
